import React, { useState } from "react";
import { CORETooltip } from "../../COREDesignSystem/Overlay/CORETooltip";
import { TestIDWrapper } from "../testids/testids";
import {
  COREIcon,
  COREIconProps,
} from "../../COREDesignSystem/Content/COREIcon";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { grey100 } from "../../COREDesignSystem/Content/COREColour";
import { COREBody } from "../../COREDesignSystem/Typography/COREBody";
import { CORELink } from "../../COREDesignSystem/Action/CORELink";
import { useOpenClose } from "../global";
import {
  SubscriptionUpgradeModal,
  SubscriptionUpgradeProps,
} from "../modals/SubscriptionUpgradeModal";
import {
  ReportUnlockModal,
  ReportUnlockModalProps,
} from "../modals/ReportUnlockModal";

export type ContentLockedTooltipProps = Partial<
  Pick<ReportUnlockModalProps, "requestedContentId" | "contentType">
> &
  Pick<SubscriptionUpgradeProps, "testID" | "upgradeType" | "showHint"> & {
    displayTooltip?: boolean;
    noFitContent?: boolean;
    /** E.g. to close a dropdown the tooltip sits in, which would otherwise paint over the modal. */
    onUpgradeClick?: () => void;
    children?: React.ReactNode;
  };

export const ContentLockedIcon: React.FC<
  Omit<COREIconProps, "icon"> & {
    inline?: boolean;
    inlineBlock?: boolean;
    contentType?: string[];
  } & ContentLockedTooltipProps
> = ({
  testID,
  inline,
  inlineBlock,
  size = "xs",
  contentType,
  upgradeType,
  showHint,
  requestedContentId,
  ...props
}) => {
  return (
    <ContentLockedTooltip
      testID={`${testID}-tooltip`}
      contentType={contentType}
      upgradeType={upgradeType}
      requestedContentId={requestedContentId}
      showHint={showHint}
      displayTooltip={true}
    >
      <TestIDWrapper testID={testID} inline={inline} inlineBlock={inlineBlock}>
        <COREIcon
          icon={icon({ name: "lock", style: "regular" })}
          size={size}
          color={grey100}
          {...props}
        />
      </TestIDWrapper>
    </ContentLockedTooltip>
  );
};

export const ContentLockedTooltip: React.FC<ContentLockedTooltipProps> = ({
  testID,
  contentType,
  children,
  upgradeType,
  requestedContentId,
  showHint,
  displayTooltip,
  noFitContent = false,
  onUpgradeClick,
}) => {
  const { opener, isOpen, closer } = useOpenClose(false);
  const [isHide, setIsHide] = useState<boolean | undefined>(false);
  const {
    opener: openerReportUnlock,
    isOpen: isReportUnlockOpen,
    closer: closerReportUnlock,
  } = useOpenClose(false);

  if (!displayTooltip) {
    return <>{children}</>;
  }
  const UnlockContentMessage = (
    <>
      <COREBody type="p3" inline marginBottom={false}>
        {" "}
        or{" "}
      </COREBody>
      <CORELink
        type="p3"
        testID={`${testID}-unlock-content`}
        underline={true}
        inline={true}
        onClick={openerReportUnlock}
      >
        unlock this {contentType?.join()}
      </CORELink>
      <COREBody type="p3" inline marginBottom={false}>
        {" "}
        to gain access.
      </COREBody>
    </>
  );
  return (
    <>
      <CORETooltip
        title={""}
        message={
          <>
            <COREBody type="p3" inline marginBottom={false}>
              This content is not available with your current subscription.{" "}
            </COREBody>
            <CORELink
              type="p3"
              testID={`${testID}-upgrade-subscription`}
              underline={true}
              inline={true}
              onClick={() => {
                opener();
                setIsHide(true);
                onUpgradeClick?.();
              }}
            >
              Upgrade your subscription
            </CORELink>
            {contentType ? (
              UnlockContentMessage
            ) : (
              <COREBody type="p3" inline marginBottom={false}>
                {" "}
                to access this content.
              </COREBody>
            )}
          </>
        }
        position="top"
        width="234px"
        testID={testID}
        destroyTooltipOnHide={true}
        hidden={isHide}
        noFitContent={noFitContent}
      >
        <div
          onMouseOver={() => {
            setIsHide(false);
          }}
        >
          {children}
        </div>
      </CORETooltip>
      <SubscriptionUpgradeModal
        testID={`${testID}-subscription-upgrade-modal`}
        isOpen={isOpen}
        closer={closer}
        upgradeType={upgradeType}
        showHint={showHint}
      />
      {contentType && requestedContentId && (
        <ReportUnlockModal
          testID={`${testID}-report-unlock-modal`}
          isOpen={isReportUnlockOpen}
          closer={closerReportUnlock}
          requestedContentId={requestedContentId}
          contentType={contentType}
        />
      )}
    </>
  );
};
